import { createBrowserRouter, RouterProvider, Navigate, ScrollRestoration, Outlet, useMatch } from "react-router-dom";
import LoginPage from "./pages/LoginPage";
import QueuePage from "./pages/QueuePage";
import AccountSettingPage from "./pages/AccountSettingPage";
import ApiKeyManagementPage from "./pages/ApiKeyManagementPage";
import TeamManagementPage from "./pages/TeamManagementPage";
import CallHistoryPage from "./pages/CallHistoryPage";
import CallHistoryDetailPage from "./pages/CallHistoryDetailPage";
import CallStatisticsPage from "./pages/CallStatisticsPage";
import MobileCallLogsPage from "./pages/MobileCallLogsPage";
import ContactPage from "./pages/ContactPage";
import ContactDetailPage from "./pages/ContactDetailPage";
import RoutingPhonePage from "./pages/RoutingPhonePage";
import RoutingAudioPage from "./pages/RoutingAudioPage";
import RoutingIvrPage from "./pages/RoutingIvrPage";
import KakaoTemplateManagementPage from "./pages/KakaoTemplateManagementPage";
import MessageHistoryPage from "./pages/MessageHistoryPage";
import SmsTemplateManagementPage from "./pages/SmsTemplateManagementPage";
import CallManualPage from "./pages/CallManualPage";
import NotFoundPage from "./pages/NotFoundPage";
import React from "react";
import { ThemeProvider } from "styled-components";
import { darkTheme, lightTheme } from "./styles/theme";
import { consoleDarkTheme, consoleLightTheme } from "./styles/consoleTheme";
import useTheme from "./hooks/useTheme";
import MainLayout from "./components/common/MainLayout";
import ConfirmModal from "./components/common/ConfirmModal";
import { hasCorePermission } from "./components/common/navigation";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

const themeDefaultValue = {
  theme: "light",
  onChangeTheme: () => {},
}

export const CustomThemeContext = React.createContext(themeDefaultValue);

export const ProtectedRoute = ({ children, requiredPermission }: { children: React.ReactNode; requiredPermission?: string }) => {
  const { isAuthenticated, isLoading, mode, user, coreUser } = useAuth();

  if (isLoading) {
    return null; // Or a loading spinner
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredPermission && !hasCorePermission(mode, user, coreUser, requiredPermission)) {
    return <Navigate to="/statistics/calls" replace />;
  }

  return <>{children}</>;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (isAuthenticated) {
    return <Navigate to="/statistics/calls" replace />;
  }

  return <>{children}</>;
};

const router = createBrowserRouter([
  {
    element: <RouteUiFrame />,
    children: [
      {
        path: "/login",
        element: <PublicRoute><LoginPage /></PublicRoute>,
      },
      {
        path: "/ringing-groups",
        element: (
          <ProtectedRoute>
            <MainLayout>
              <QueuePage />
            </MainLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: "/account",
        element: (
          <ProtectedRoute>
            <MainLayout>
              <AccountSettingPage />
            </MainLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: "/settings/api-keys",
        element: (
          <ProtectedRoute>
            <MainLayout>
              <ApiKeyManagementPage />
            </MainLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: "/teams",
        element: (
          <ProtectedRoute>
            <MainLayout>
              <TeamManagementPage />
            </MainLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: "/contact",
        element: (
          <ProtectedRoute>
            <MainLayout>
              <ContactPage />
            </MainLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: "/contact/:id",
        element: (
          <ProtectedRoute>
            <MainLayout>
              <ContactDetailPage />
            </MainLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: "/routing/phone",
        element: (
          <ProtectedRoute>
            <MainLayout>
              <RoutingPhonePage />
            </MainLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: "/routing/audio",
        element: (
          <ProtectedRoute>
            <MainLayout>
              <RoutingAudioPage />
            </MainLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: "/routing/ivr",
        element: (
          <ProtectedRoute>
            <MainLayout>
              <RoutingIvrPage />
            </MainLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: "/messages/history",
        element: <ProtectedRoute><MainLayout><MessageHistoryPage /></MainLayout></ProtectedRoute>,
      },
      {
        path: "/messages/alimtalk-templates",
        element: <ProtectedRoute><MainLayout><KakaoTemplateManagementPage /></MainLayout></ProtectedRoute>,
      },
      {
        path: "/messages/sms-templates",
        element: <ProtectedRoute><MainLayout><SmsTemplateManagementPage /></MainLayout></ProtectedRoute>,
      },
      {
        path: "/statistics/call-history",
        element: (
          <ProtectedRoute>
            <MainLayout>
              <CallHistoryPage />
            </MainLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: "/statistics/call-history/:id",
        element: (
          <ProtectedRoute>
            <MainLayout>
              <CallHistoryDetailPage />
            </MainLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: "/statistics/calls",
        element: (
          <ProtectedRoute>
            <MainLayout>
              <CallStatisticsPage />
            </MainLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: "/statistics/mobile-logs",
        element: (
          <ProtectedRoute requiredPermission="MOBILE_CALL_LOG_READ">
            <MainLayout>
              <MobileCallLogsPage />
            </MainLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: "/etc/call/manual",
        element: (
          <ProtectedRoute>
            <MainLayout>
              <CallManualPage />
            </MainLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: "/",
        element: <Navigate to="/statistics/calls" replace />,
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ]
  }
]);

export function RouteUiFrame() {
  const isLoginSurface = Boolean(useMatch({ path: "/login", end: true, caseSensitive: false }));
  const themeProps = React.useContext(CustomThemeContext);
  const activeTheme = isLoginSurface
    ? (themeProps.theme === "light" ? lightTheme : darkTheme)
    : (themeProps.theme === "light" ? consoleLightTheme : consoleDarkTheme);
  const [showForbiddenModal, setShowForbiddenModal] = React.useState(false);

  React.useEffect(() => {
    const handleForbidden = () => {
      setShowForbiddenModal(true);
    };

    window.addEventListener('api-forbidden', handleForbidden);
    return () => {
      window.removeEventListener('api-forbidden', handleForbidden);
    };
  }, []);

  return (
    <ThemeProvider
      theme={activeTheme}
    >
      <Toaster
        position="top-right"
        toastOptions={{
          className: '',
          duration: 4000,
          style: {
            background: isLoginSurface ? (themeProps.theme === 'dark' ? '#1E2030' : '#FFFFFF') : activeTheme.color.primary_100,
            color: isLoginSurface ? (themeProps.theme === 'dark' ? '#E2E8F0' : '#1E293B') : activeTheme.color.text_color_100,
            padding: isLoginSurface ? '16px 24px' : '12px 16px',
            borderRadius: isLoginSurface ? '16px' : '8px',
            fontSize: isLoginSurface ? '15px' : '13px',
            fontWeight: '500',
            boxShadow: themeProps.theme === 'dark'
              ? '0 10px 30px -5px rgba(0, 0, 0, 0.5), 0 4px 10px -5px rgba(0, 0, 0, 0.5)'
              : '0 10px 30px -5px rgba(0, 0, 0, 0.1), 0 4px 10px -5px rgba(0, 0, 0, 0.05)',
            border: themeProps.theme === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.05)',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: '#FFFFFF',
            },
            style: {
              borderLeft: '5px solid #10B981',
            }
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: '#FFFFFF',
            },
            style: {
              borderLeft: '5px solid #EF4444',
            }
          },
        }}
      />
      <div lang={isLoginSurface ? undefined : "ko"} data-ui-scope={isLoginSurface ? 'login' : 'console'} style={{ minWidth: isLoginSurface ? 1280 : 1024, color: activeTheme.color.text_color_100, background: activeTheme.color.primary_200 }}>
        <ScrollRestoration />
        <Outlet />
      </div>

      {showForbiddenModal && (
        <ConfirmModal
          appearance={isLoginSurface ? 'legacy' : 'console'}
          title="접근 권한 없음"
          message="해당 메뉴에 대한 접근 권한이 없습니다. 관리자에게 문의하세요."
          onConfirm={() => setShowForbiddenModal(false)}
          isSingleButton={true}
        />
      )}
    </ThemeProvider>
  );
}

function App() {
  const themeProps = useTheme();

  return (
    <div>
      <CustomThemeContext.Provider value={themeProps}>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </CustomThemeContext.Provider>
    </div>
  );
}

export default App;
